// FlashCard.tsx

import React, { useState } from 'react';
import { Card, Button, ProgressBar, ButtonGroup } from 'react-bootstrap';
import { Plus, Dash, CheckCircleFill } from 'react-bootstrap-icons';
import { FlashCardItem } from './dataDocuments'; 

// Define the props for the FlashCard component
interface FlashCardProps {
  card: FlashCardItem;
  onUpdateConfidence: (id: number, newConfidence: number) => void;
}

const FlashCard: React.FC<FlashCardProps> = ({ card, onUpdateConfidence }) => {
  // isFlipped is reset to false (hidden) whenever the component remounts (e.g., on shuffle)
  const [isFlipped, setIsFlipped] = useState(false);

  // Function to handle confidence level update (increment, decrement, or set a specific value)
  const handleConfidenceChange = (newConfidenceTarget: number | 'increment' | 'decrement') => {
    let newConfidence: number;

    if (newConfidenceTarget === 'increment') {
      newConfidence = Math.min(100, card.confidence + 1);
    } else if (newConfidenceTarget === 'decrement') {
      newConfidence = Math.max(0, card.confidence - 1);
    } else {
      // Set the confidence directly to the target percentage
      newConfidence = newConfidenceTarget;
    }
    
    onUpdateConfidence(card.id, newConfidence);
  };

  // Do not render the card if confidence is 100%
  if (card.confidence === 100) {
    return null;
  }

  return (
    <Card 
      className="mb-3 shadow-sm" 
      // Click the card body (but not the buttons within) to flip the card
      onClick={() => setIsFlipped(!isFlipped)} 
      style={{ cursor: 'pointer', minHeight: '180px' }}
    >
      <Card.Body className="d-flex flex-column justify-content-between">
        
        {/* Card Content (Term or Definition) */}
        <Card.Title className="text-center h4 text-primary">
          {card.term}
        </Card.Title>
        
        {/* Category Badge */}
        <div className="text-center mb-1">
          <small className="text-muted">Category: **{card.category}**</small>
        </div>

        {/* Definition Content - Using dangerouslySetInnerHTML to render the HTML */}
        <div className="text-center mt-2" style={{ minHeight: '50px' }}>
          {isFlipped ? (
            <p className="text-dark" dangerouslySetInnerHTML={{ __html: card.definition }} />
          ) : (
            <p className="text-muted fst-italic">Click to see definition</p>
          )}
        </div>

        {/* Confidence Progress Bar */}
        <ProgressBar now={card.confidence} label={`${card.confidence}%`} variant="info" className="mt-2" />

        {/* Confidence Control Buttons */}
        <div className="mt-3">
            {/* Fine Tune Buttons (+1% and -1%) */}
            <div className="d-flex justify-content-center mb-2">
                <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    className="me-2"
                    // e.stopPropagation() prevents the card from flipping when button is clicked
                    onClick={(e) => { e.stopPropagation(); handleConfidenceChange('decrement'); }}
                    disabled={card.confidence === 0} 
                >
                    <Dash /> -1%
                </Button>
                <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); handleConfidenceChange('increment'); }}
                    disabled={card.confidence === 100}
                >
                    +1% <Plus />
                </Button>
            </div>

            {/* Chunk Buttons (25%, 50%, 75%, 99%, 100%) - Enabled for upgrade and downgrade */}
            <ButtonGroup className="w-100">
                <Button 
                    variant="outline-danger" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); handleConfidenceChange(25); }}
                >
                    25%
                </Button>
                <Button 
                    variant="outline-warning" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); handleConfidenceChange(50); }}
                >
                    50%
                </Button>
                <Button 
                    variant="outline-info" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); handleConfidenceChange(75); }}
                >
                    75%
                </Button>
                <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); handleConfidenceChange(99); }}
                >
                    99%
                </Button>
                <Button 
                    variant="outline-success" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); handleConfidenceChange(100); }}
                    disabled={card.confidence === 100}
                >
                    <CheckCircleFill /> 100%
                </Button>
            </ButtonGroup>
        </div>
      </Card.Body>
    </Card>
  );
};

export default FlashCard;